import type { ScormExportContext } from './accountAssignmentApi';
import { buildPublishDeployTargets, fetchExportPackageInfo, fetchLessonPublishState, PublishApiError } from './publishApi';
import {
  buildCourseDeployPackage, buildLessonDeployPackage, deployPackagesByAccount, reportDeployRunFailures,
  isFatalPublishingError,
  type deployCoursePackage, type DeployAccountInput, type DeployAccountResult, type DeployClientFailure, type DeployLedgerInfo,
} from './courseDeployApi';
import { loadCoursePointers } from './exportSourceLoader';
import { healReferencedLessonPackages, prepareLessonHealTargets } from './lessonPackageHeal';

export interface LessonDeploymentPreparation {
  accounts: DeployAccountInput[];
  deployPackage: typeof deployCoursePackage;
  contentPublishedAt?: string;
}

async function prepareCourseLessonPackages({ packageId, targets, exportedBy, prepareLesson, describeLesson, ledgerFor, results }: {
  packageId: string;
  targets: DeployAccountInput[];
  exportedBy: string;
  prepareLesson?: (id: string, accounts: DeployAccountInput[]) => Promise<LessonDeploymentPreparation>;
  describeLesson: (id: string) => string;
  ledgerFor: (packageKind: 'course' | 'lesson') => DeployLedgerInfo;
  results: DeployAccountResult[];
}): Promise<Set<string>> {
  // Append each completed group immediately so a later failure retains its results.
  const refGroups: Array<{ refIds: string[]; groupTargets: DeployAccountInput[] }> = [];
  const sharedTargets = targets.filter((target) => target.base === true || target.hasCustomizations !== true);
  if (sharedTargets.length > 0) {
    const basePointers = await loadCoursePointers(packageId, null);
    refGroups.push({
      refIds: basePointers[0]?.lessonRefs.map((ref) => ref.id) ?? [],
      groupTargets: sharedTargets,
    });
  }
  for (const target of targets) {
    if (target.base === true || target.hasCustomizations !== true) continue;
    const accountPointers = await loadCoursePointers(packageId, target.externalAccountId ?? null);
    refGroups.push({
      refIds: accountPointers[0]?.lessonRefs.map((ref) => ref.id) ?? [],
      groupTargets: [target],
    });
  }

  const blockedSlugs = new Set<string>();
  const healDeps = {
    fetchPackageInfo: (lessonId: string, accountSlug?: string) =>
      fetchExportPackageInfo(lessonId, accountSlug, 'reference'),
    fetchLessonPublishState,
    deployLesson: async (lessonId: string, accounts: DeployAccountInput[], lessonPublishedAt?: string) => {
      let preparation;
      try {
        preparation = prepareLesson
          ? await prepareLesson(lessonId, accounts)
          : { accounts: await prepareLessonHealTargets(lessonId, accounts) };
      } catch (error) {
        if (isFatalPublishingError(error)) throw error;
        return accounts.map(account => ({ accountSlug: account.accountSlug, success: false, error: error instanceof Error ? error.message : 'Lesson preparation failed.' }));
      }
      const lessonResponse = await deployPackagesByAccount({
        packageId: lessonId,
        accounts: preparation.accounts,
        deployPackage: preparation.deployPackage,
        buildPackage: (externalAccountId) =>
          buildLessonDeployPackage({
            lessonId,
            exportedBy: exportedBy,
            accountId: externalAccountId,
          }),
        onPhase: () => {},
        contentPublishedAt: preparation.contentPublishedAt ?? lessonPublishedAt,
        ledger: ledgerFor('lesson'),
      });
      return lessonResponse.results;
    },
    describeLesson: describeLesson,
  };
  for (const { refIds, groupTargets } of refGroups) {
    const heal = await healReferencedLessonPackages(refIds, groupTargets, healDeps);
    results.push(...heal.results);
    for (const slug of heal.blockedSlugs) blockedSlugs.add(slug);
  }
  return blockedSlugs;
}

/** Shared browser fan-out. Editor lifecycle, autosave, modal state and polling
 * ownership stay with the caller; maintenance supplies guarded transport. */
export async function runPublishDelivery({
  contentKind, packageId, exportedBy, contentPublishedAt, onlyAccountId, clientContext,
  enforceSequence = false, loadContext, onProgress, onResults, onTargetCount,
  onVerifyTargets, onSnapshotChanged, onRunRecorded, onParentSnapshotsChanged,
  describeLesson = (id: string) => id, deployPackage, prepareLesson,
}: {
  contentKind: 'course' | 'lesson';
  packageId: string;
  exportedBy: string;
  contentPublishedAt?: string;
  onlyAccountId?: number;
  clientContext?: { accountId: number; accountName: string } | null;
  enforceSequence?: boolean;
  loadContext: () => Promise<ScormExportContext | undefined>;
  onProgress: (message: string) => void;
  onResults: (results: DeployAccountResult[]) => void;
  onTargetCount: (count: number) => void;
  onVerifyTargets: (targets: Array<{ accountSlug: string; deployId?: string }>) => void;
  onSnapshotChanged: () => void;
  onRunRecorded: (runId: string) => void;
  onParentSnapshotsChanged: () => void;
  describeLesson?: (id: string) => string;
  deployPackage?: typeof deployCoursePackage;
  prepareLesson?: (id: string, accounts: DeployAccountInput[]) => Promise<LessonDeploymentPreparation>;
}): Promise<void> {
    // The cached scorm-context can predate the save/publish that just ran —
    // stale customization flags deploy the wrong package shape, and a stale
    // account list can silently drop the requested scope. Always refetch
    // after the lifecycle write. NOTE: TanStack Query's refetch() does NOT
    // reject on failure — it RESOLVES with status 'error' and the PREVIOUS
    // data still populated, so the error flag must be checked explicitly or
    // a failed refresh silently deploys from the stale snapshot.
    onVerifyTargets([]);
    onTargetCount(0);
    onProgress('Loading account list…');
    let context;
    try {
      context = await loadContext();
    } catch {
      // fall through to the guard below
    }
    // Fan-outs that die BEFORE targeting get a durable RUN-LEVEL error (an
    // intent-0 run derives complete-with-failures immediately) — never a
    // fabricated scope outcome, which would blame the wrong account context.
    // Returns the run id so the freshness loop can pin it.
    const reportPreTargetingFailure = async (message: string): Promise<string> => {
      const failureRunId = crypto.randomUUID();
      await reportDeployRunFailures(packageId, {
        runId: failureRunId,
        runMeta: {
          intendedScopeCount: 0,
          initiatedAccountId: clientContext?.accountId ?? null,
          contentKind,
          contentId: packageId,
          ...(contentPublishedAt ? { contentPublishedAt } : {}),
        },
        packageKind: contentKind,
      }, [], { runError: message, contentPublishedAt });
      return failureRunId;
    };
    const settlePreTargetingFailure = async (message: string, accountSlug: string): Promise<void> => {
      onResults([{ accountSlug, success: false, error: message }]);
      const failureRunId = await reportPreTargetingFailure(message);
      onRunRecorded(failureRunId);
      onParentSnapshotsChanged();
    };

    if (!context) {
      // Context-accurate copy: a client-context update failing here affected
      // ONE account — "(all)"/anywhere phrasing would overstate it.
      const message = clientContext
        ? `Account list unavailable — the update for ${clientContext.accountName} could not be delivered. Publish an update to retry.`
        : 'Account list unavailable — the content was published but could not be delivered. Publish an update to retry.';
      await settlePreTargetingFailure(message, clientContext?.accountName ?? '(all)');
      return;
    }
    let targets: DeployAccountInput[];
    try {
      targets = buildPublishDeployTargets(context, { enforceSequence, onlyAccountId });
    } catch (error) {
      if (!(error instanceof PublishApiError) || error.code !== 'MISSING_ACCOUNT_CONTENT_ID') {
        throw error;
      }
      // The lifecycle write has already committed. Record the pre-targeting
      // failure so delivery status cannot keep presenting the previous run as
      // the latest settled result during an author-api/frontend version skew.
      await settlePreTargetingFailure(error.message, clientContext?.accountName ?? '(all)');
      return;
    }
    if (targets.length === 0) {
      if (onlyAccountId !== undefined) {
        // Fail closed: a REQUESTED account scope missing from the refreshed
        // assignment list must never read as an empty success — nothing was
        // deployed for it. (Full publishes always include the base target.)
        const message = 'This account context is not in the current assignment list — nothing was deployed. Refresh the page, then publish an update.';
        await settlePreTargetingFailure(message, clientContext?.accountName ?? '(account)');
        return;
      }
      onResults([]);
      return;
    }
    onTargetCount(targets.length);
    onProgress('Publishing…');
    const results: DeployAccountResult[] = [];

    // delivery-status run recording: one client-generated run groups every
    // deploy POST of this fan-out — heal-built lesson packages included — with
    // the declared scope intent the ledger derives completion/stall from.
    const runId = crypto.randomUUID();
    const ledgerFor = (packageKind: 'course' | 'lesson'): DeployLedgerInfo => ({
      runId,
      runMeta: {
        intendedScopeCount: targets.length,
        initiatedAccountId: clientContext?.accountId ?? null,
        contentKind,
        contentId: packageId,
        // The RUN's revision on every request — a heal lesson POST may be the
        // only one this run ever makes.
        ...(contentPublishedAt ? { contentPublishedAt } : {}),
      },
      packageKind,
    });
    const refreshDeliverySnapshot = () => {
      onSnapshotChanged();
    };

    // Heal step (referenced-lesson-packages): the course pointer references
    // each lesson's hosted package, and lessons published by the pre-pointer
    // cascade never got standalone packages — every (lesson, scope) pair is
    // existence-checked and any missing package is built and deployed BEFORE
    // the pointer ships. Customized accounts deploy ACCOUNT-RESOLVED pointers
    // whose ref sets can differ from base (client-added/suppressed lessons),
    // so each ref set is healed against exactly the targets that will host
    // it. Heal refusals/failures BLOCK the pointer for the affected scopes
    // (fail closed): a hosted pointer must never reference a missing package,
    // and a heal must never build a lesson whose live rows aren't its
    // published content (see lessonPackageHeal).
    let pointerTargets = targets;
    if (contentKind === 'course') {
      onProgress('Preparing lessons…');
      try {
        const blockedSlugs = await prepareCourseLessonPackages({
          packageId, targets, exportedBy, prepareLesson, describeLesson, ledgerFor, results,
        });
        if (blockedSlugs.size > 0) {
          pointerTargets = targets.filter((target) => !blockedSlugs.has(target.accountSlug));
          const withheldFailures: DeployClientFailure[] = [];
          for (const target of targets) {
            if (!blockedSlugs.has(target.accountSlug)) continue;
            const message =
              'Course package withheld for this scope: its referenced lesson packages could not be prepared (see the lesson rows above). Resolve the lesson issue, then publish an update.';
            results.push({ accountSlug: target.accountSlug, success: false, error: message });
            // The durable copy embeds the scope's lesson refusal details —
            // "the lesson rows above" only exist in this tab. Heal rows carry
            // display text in accountSlug; scopeSlug is the raw match key.
            const lessonDetails = results
              .filter((row) => (row.scopeSlug ?? row.accountSlug) === target.accountSlug
                && !row.success && row.error && row.error !== message)
              .map((row) => row.error)
              .join(' ');
            if (results.some(row => row.scopeSlug === target.accountSlug && row.failureReporting === 'server')) continue;
            withheldFailures.push({
              accountSlug: target.accountSlug,
              externalAccountId: target.externalAccountId ?? null,
              error: lessonDetails
                ? `Course package withheld — its referenced lesson packages could not be prepared: ${lessonDetails} Resolve the lesson issue, then publish an update.`
                : message,
            });
          }
          // Withheld pointers never produce a deploy POST — record durably
          // (awaited: fire-and-forget dies with the tab).
          if (withheldFailures.length > 0) {
            await reportDeployRunFailures(packageId, ledgerFor(contentKind), withheldFailures, { contentPublishedAt });
          }
        }
      } catch (error) {
        if (deployPackage && isFatalPublishingError(error)) throw error;
        // Fail closed: without a verified heal, no scope gets the pointer.
        const message = `Course package not deployed — lesson packages could not be verified: ${
          error instanceof Error ? error.message : 'unknown error'
        } Publish an update to retry.`;
        results.push({ accountSlug: '(lessons)', success: false, error: message });
        await reportDeployRunFailures(packageId, ledgerFor(contentKind), targets.map((target) => ({
          accountSlug: target.accountSlug,
          externalAccountId: target.externalAccountId ?? null,
          error: message,
        })), { contentPublishedAt });
        onResults(results);
        refreshDeliverySnapshot();
        onRunRecorded(runId);
        onParentSnapshotsChanged();
        return;
      }
      if (pointerTargets.length === 0) {
        onResults(results);
        refreshDeliverySnapshot();
        onRunRecorded(runId);
        onParentSnapshotsChanged();
        return;
      }
    }

    const buildPackage = (externalAccountId: number | null) =>
      contentKind === 'course'
        ? buildCourseDeployPackage({
            courseId: packageId,
            exportedBy: exportedBy,
            accountId: externalAccountId,
          })
        : buildLessonDeployPackage({
            lessonId: packageId,
            exportedBy: exportedBy,
            accountId: externalAccountId,
          });
    const response = await deployPackagesByAccount({
      packageId,
      accounts: pointerTargets,
      deployPackage,
      buildPackage,
      onPhase: (phase) => onProgress(phase === 'building' ? 'Preparing content…' : 'Publishing…'),
      contentPublishedAt,
      ledger: ledgerFor(contentKind),
    });
    onResults([...results, ...response.results]);
    onVerifyTargets(response.results
      .filter((result) => result.success)
      .map((result) => ({ accountSlug: result.accountSlug, deployId: result.deployId })));
    refreshDeliverySnapshot();
    // Bounded freshness loop: keep force-fetching until the snapshot
    // acknowledges THIS run — an in-flight or other-instance cache can
    // otherwise hand back a pre-publish "settled" fleet that stops polling.
    onRunRecorded(runId);
    onParentSnapshotsChanged();
}
